import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Reveal } from "./Reveal";
import { scrollTo } from "./Navbar";
import { VINTAGE_OPTIONS, TURNOVER_OPTIONS, YESNO, LOAN_NEED_OPTIONS } from "@/lib/site";

const Field = ({ label, options, value, onChange, testid }) => (
  <div>
    <label className="text-sm font-semibold text-navy">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="mt-2 h-12 rounded-xl border-black/10 bg-surface focus:ring-2 focus:ring-brand/50">
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const Eligibility = () => {
  const [f, setF] = useState({ vintage: "", turnover: "", gst: "", itr: "", loan: "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const complete = Object.values(f).every(Boolean);
  const eligible = complete && f.vintage !== "Less than 1 year" && f.gst === "Yes" && f.itr === "Yes";

  return (
    <div className="bg-white rounded-3xl p-8 lg:p-10 border border-black/5 tick-shadow">
      <h3 className="font-display font-bold text-2xl text-navy">Eligibility Checker</h3>
      <p className="text-sm text-ink/55 mt-1">Answer a few quick questions to see where you stand.</p>

      <div className="mt-7 grid sm:grid-cols-2 gap-5">
        <Field label="Business Vintage" options={VINTAGE_OPTIONS} value={f.vintage} onChange={set("vintage")} testid="elig-vintage" />
        <Field label="Annual Turnover" options={TURNOVER_OPTIONS} value={f.turnover} onChange={set("turnover")} testid="elig-turnover" />
        <Field label="GST Registered" options={YESNO} value={f.gst} onChange={set("gst")} testid="elig-gst" />
        <Field label="ITR Filed" options={YESNO} value={f.itr} onChange={set("itr")} testid="elig-itr" />
        <div className="sm:col-span-2">
          <Field label="Required Loan Amount" options={LOAN_NEED_OPTIONS} value={f.loan} onChange={set("loan")} testid="elig-loan" />
        </div>
      </div>

      <AnimatePresence>
        {complete && (
          <motion.div
            initial={{ opacity: 0, y: 10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 overflow-hidden"
            data-testid="elig-result"
          >
            <div className={`rounded-2xl p-5 flex items-start gap-3 ${eligible ? "bg-emerald/10" : "bg-brand/10"}`}>
              {eligible ? <CheckCircle2 className="w-6 h-6 text-emerald shrink-0" /> : <Sparkles className="w-6 h-6 text-brand shrink-0" />}
              <div>
                <p className={`font-display font-bold ${eligible ? "text-emerald" : "text-brand"}`}>
                  {eligible ? "You May Be Eligible!" : "Let's explore your options"}
                </p>
                <p className="text-sm text-ink/60 mt-1">
                  {eligible
                    ? "Based on your inputs, you likely qualify for funding. Book a free consultation to get started."
                    : "Your profile may still qualify with the right lender. Talk to our advisors for a tailored assessment."}
                </p>
                <button onClick={() => scrollTo("contact")} className="mt-3 text-sm font-semibold text-brand hover:text-navy transition-colors">
                  Book Free Consultation →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const formatINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

const SliderRow = ({ label, value, val, setVal, min, max, step, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-semibold text-navy">{label}</span>
      <span className="text-sm font-display font-bold text-brand">{value}</span>
    </div>
    <Slider data-testid={testid} value={val} onValueChange={setVal} min={min} max={max} step={step} className="[&_[role=slider]]:border-brand [&>span>span]:bg-brand" />
  </div>
);

const Calculator = () => {
  const [amount, setAmount] = useState([2500000]);
  const [rate, setRate] = useState([14]);
  const [tenure, setTenure] = useState([36]);

  const { emi, total, interest } = useMemo(() => {
    const P = amount[0];
    const r = rate[0] / 12 / 100;
    const n = tenure[0];
    const e = r === 0 ? P / n : (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const t = e * n;
    return { emi: Math.round(e), total: Math.round(t), interest: Math.round(t - P) };
  }, [amount, rate, tenure]);

  return (
    <div className="grid lg:grid-cols-2 gap-8 items-stretch">
      <div className="bg-white rounded-3xl p-8 lg:p-10 border border-black/5 tick-shadow flex flex-col justify-center gap-8">
        <SliderRow label="Loan Amount" value={`₹${formatINR(amount[0])}`} val={amount} setVal={setAmount} min={500000} max={50000000} step={100000} testid="calc-amount" />
        <SliderRow label="Interest Rate (p.a.)" value={`${rate[0]}%`} val={rate} setVal={setRate} min={8} max={24} step={0.5} testid="calc-rate" />
        <SliderRow label="Tenure" value={`${tenure[0]} months`} val={tenure} setVal={setTenure} min={6} max={84} step={6} testid="calc-tenure" />
      </div>

      <div className="rounded-3xl p-8 lg:p-10 bg-navy text-white relative overflow-hidden grain">
        <div className="blob w-64 h-64 bg-brand/30 -top-10 -right-10" />
        <div className="relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">Estimated Monthly EMI</p>
          <div className="mt-3 font-display font-black text-5xl" data-testid="calc-emi">₹{formatINR(emi)}</div>
          <div className="mt-8 grid grid-cols-2 gap-5">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <p className="text-xs text-white/50">Total Interest</p>
              <p className="mt-1 font-display font-bold text-xl">₹{formatINR(interest)}</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <p className="text-xs text-white/50">Total Payable</p>
              <p className="mt-1 font-display font-bold text-xl">₹{formatINR(total)}</p>
            </div>
          </div>
          <p className="mt-6 text-xs text-white/40">Indicative only. Actual EMI depends on lender terms and final sanction.</p>
          <button onClick={() => scrollTo("contact")} className="mt-6 w-full py-3.5 rounded-full bg-brand hover:bg-emerald text-white font-semibold transition-colors" data-testid="calc-cta">
            Get Your Exact Offer
          </button>
        </div>
      </div>
    </div>
  );
};

export const Tools = () => {
  return (
    <section id="tools" data-testid="tools" className="py-24 lg:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-4">04 — Check &amp; Calculate</p>
          <h2 className="font-display font-extrabold text-navy tracking-tight text-4xl md:text-5xl max-w-2xl leading-[1.05]">
            Know your eligibility and EMI in seconds.
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-start mb-8">
          <Reveal><Eligibility /></Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mb-3">
            <h3 className="font-display font-bold text-2xl text-navy">Loan / EMI Calculator</h3>
          </div>
          <Calculator />
        </Reveal>
      </div>
    </section>
  );
};
